import { QuantitySelectorComponent } from '@theme/component-quantity-selector';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * @extends {QuantitySelectorComponent}
 */
class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  /** @type {(() => void) | null} */
  #reposition = null;

  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max; // Cart uses absolute max, not max minus cart quantity
  }

  /**
   * The cart (especially the drawer) renders this inside a scrollable container
   * (`overflow: auto`), which clips the absolutely-positioned options popover.
   * Promote the popover to `position: fixed` on open so it escapes that clipping.
   * Because the drawer dialog is transformed (it establishes the containing block
   * for fixed elements), coordinates are computed relative to that block. The list
   * stays in the DOM here, so its option `on:click` bindings keep working.
   * @param {Event} event
   */
  handleSelectToggle(event) {
    super.handleSelectToggle(event);

    const details = event.target;
    if (!(details instanceof HTMLDetailsElement)) return;

    const list = this.refs.quantitySelectList;
    if (!(list instanceof HTMLElement)) return;

    if (details.open) {
      this.#position(list);
      this.#reposition = () => {
        if (details.open) this.#position(list);
      };
      window.addEventListener('scroll', this.#reposition, { capture: true, passive: true });
      window.addEventListener('resize', this.#reposition, { passive: true });
    } else {
      this.#teardown(list);
    }
  }

  /**
   * Nearest ancestor that establishes a containing block for `position: fixed`
   * (a transform / filter / perspective / will-change: transform). Returns null
   * when there is none (then `fixed` is relative to the viewport).
   * @param {HTMLElement} el
   * @returns {HTMLElement | null}
   */
  #containingBlock(el) {
    let node = el.parentElement;
    while (node && node !== document.documentElement) {
      const cs = getComputedStyle(node);
      if (
        cs.transform !== 'none' ||
        cs.filter !== 'none' ||
        cs.perspective !== 'none' ||
        cs.willChange.includes('transform')
      ) {
        return node;
      }
      node = node.parentElement;
    }
    return null;
  }

  /**
   * @param {HTMLElement} list - The options popover (<ul>).
   */
  #position(list) {
    const wrapper = this.querySelector('.quantity-select-wrapper');
    if (!(wrapper instanceof HTMLElement)) return;

    const cbEl = this.#containingBlock(list);
    const cb = cbEl ? cbEl.getBoundingClientRect() : { top: 0, left: 0 };
    const a = wrapper.getBoundingClientRect();

    list.style.position = 'fixed';
    list.style.right = 'auto';
    list.style.margin = '0';
    list.style.minWidth = `${a.width}px`;
    list.style.maxHeight = '';
    list.style.zIndex = '1000';

    const r = list.getBoundingClientRect();
    const spaceBelow = window.innerHeight - a.bottom - 8;
    const spaceAbove = a.top - 8;

    let topViewport;
    let maxHeight;
    if (r.height <= spaceBelow || spaceBelow >= spaceAbove) {
      topViewport = a.bottom + 4;
      maxHeight = spaceBelow;
    } else {
      topViewport = Math.max(8, a.top - Math.min(r.height, spaceAbove) - 4);
      maxHeight = spaceAbove;
    }

    const leftViewport = Math.max(8, Math.min(a.right - r.width, window.innerWidth - r.width - 8));

    list.style.top = `${topViewport - cb.top}px`;
    list.style.left = `${leftViewport - cb.left}px`;
    list.style.maxHeight = `${Math.max(120, maxHeight)}px`;
  }

  /**
   * @param {HTMLElement} list - The options popover (<ul>).
   */
  #teardown(list) {
    for (const prop of ['position', 'top', 'left', 'right', 'margin', 'min-width', 'max-height', 'z-index']) {
      list.style.removeProperty(prop);
    }
    if (this.#reposition) {
      window.removeEventListener('scroll', this.#reposition, { capture: true });
      window.removeEventListener('resize', this.#reposition);
      this.#reposition = null;
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    if (this.#reposition) {
      window.removeEventListener('scroll', this.#reposition, { capture: true });
      window.removeEventListener('resize', this.#reposition);
      this.#reposition = null;
    }
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
